import React, { useEffect, useState, useContext } from 'react';
import type { ChangeEvent } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { StaffRegisterSchema } from '../../validation/users';
import { gray100, primaryButtonBackgroundColor, red50 } from '../../constants/colors';
import { sizeBelowTenMb, textMb, titleMb, w100 } from '../../constants/location';

import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import PasswordTextfield from './PasswordTextfield';
import { common } from '../../modules/error';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../api/firebase/config';
import { addStaffUser, getUser, InviteCanceledError } from '../../api/firebase/firestore/user';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth';

const styles = {
  cardSize: {
    '@media screen and (max-width:480px)': {
      width: 343,
    },
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
  },
  cardContentPadding: {
    '&:last-child': {
      '@media screen and (max-width:480px)': {
        paddingBottom: '32px',
      },
      paddingBottom: '40px',
    },
    '@media screen and (max-width:480px)': {
      padding: '32px 16px',
    },
    padding: '40px',
  },
  required: {
    color: red50,
    marginLeft: '10px',
  },
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px',
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute',
  },
  submitButton: {
    height: '43px',
    padding: '10px 16px',
    marginTop: '8px',
    color: primaryButtonBackgroundColor,
  },
  textMb,
  titleMb,
  w100,
};

export interface RegisterStaffType {
  iconId?: string | null | undefined;
  email?: string | null | undefined;
  name: string;
  department: string;
  password: string;
  passwordConfirm: string;
  icon?: File | undefined;
}

const StaffRegisterCard: React.FC = () => {
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useContext(AuthContext);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterStaffType>({
    resolver: yupResolver(StaffRegisterSchema),
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('name') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('passwordConfirm') === undefined ||
        '' ||
        watch('department') === undefined ||
        Boolean(errors.name) ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        Boolean(errors.department) ||
        Boolean(iconSizeError)
      );
    }
    return false;
  };

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [iconSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const oobCode = urlParams.get('oobCode') ?? '';

    // メールアドレスの取得: Base64URL形式(?e=)を優先、旧形式(?email=)にも対応
    let emailParam = '';
    const encodedEmail = urlParams.get('e');

    if (encodedEmail) {
      try {
        const base64 = encodedEmail.replace(/-/g, '+').replace(/_/g, '/');
        emailParam = atob(base64);
      } catch {
        emailParam = '';
      }
    } else {
      const rawEmail = urlParams.get('email');
      emailParam = rawEmail?.split('?').join('').replace(/ /g, '+') ?? '';
    }

    if (emailParam) {
      setDefaultEmail(emailParam); // クエリパラメータを状態に設定
    }

    // oobCodeの検証: signInモードではFirebase Auth handlerが処理済みのためスキップ
    const mode = urlParams.get('mode');
    if (oobCode && mode !== 'signIn') {
      setLoading(true);
      verifyPasswordResetCode(auth, oobCode)
        .catch(error => {
          if (error instanceof FirebaseError && error.code === 'auth/expired-action-code') {
            setErrorText('認証メールの有効期限が切れています。管理者へ再招待を依頼してください。');
          } else {
            setErrorText('エラーが発生しました。');
          }
          setOpen(true);

          setTimeout(() => {
            navigate('/');
          }, 3000);
        })
        .finally(() => setLoading(false));
    }
    // 登録済み・招待取り消しの判定は、ログイン前に users を参照できないため登録ボタン押下時に行う
  }, [location, navigate]);

  useEffect(() => {
    if (defaultEmail) {
      setValue('email', defaultEmail); // react-hook-form の email フィールドに設定
    }
  }, [defaultEmail, setValue]); // defaultEmail を監視

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError('画像は10MBまでしか登録できません')
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadIconFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file && file.length > 0) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);

      setOpen(true);
      // 作成したユーザーでログイン状態になる
      const createdUser = await createUserWithEmailAndPassword(
        auth,
        defaultEmail,
        watch('password'),
      );

      if (createdUser) {
        await addStaffUser(defaultEmail, createdUser, watch, s3FileIcon);

        // Firestoreから登録済みユーザー情報を取得してAuthContextに設定
        const userData = await getUser(createdUser.user.uid);
        setCurrentUser({
          uid: createdUser.user.uid,
          name: userData?.name ?? watch('name'),
          icon: userData?.iconUrl ?? null,
          role: userData?.role ?? null,
        });

        navigate('/staff/register/complete');
      }
    } catch (error) {
      let errorText: string;
      if (error instanceof FirebaseError && error.code === 'auth/email-already-in-use') {
        errorText = 'すでに登録が完了しています';
        setTimeout(() => navigate('/'), 5000);
      } else if (error instanceof InviteCanceledError) {
        errorText = error.message;
        setTimeout(() => navigate('/'), 3000);
      } else if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setFirstSubmit(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: gray100, textAlign: 'left' }]}
                component="div"
              >
                担当者登録
              </Typography>
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={iconSizeError}
                    helperText={errors.icon?.message}
                    formMb="24px"
                    onChange={(value: React.ChangeEvent<HTMLInputElement>): void => {
                      field.onChange(value);
                      changeUploadIconFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText="メールアドレス"
                    required
                    requiredText="必須"
                    placeholder="xxx@cto-hands.co.jp"
                    disabled
                    formMb="24px"
                    sx={{ marginBottom: '24px', width: '100%' }}
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText="パスワード"
                    requiredText="必須"
                    required
                    formMb="24px"
                  />
                )}
                control={control}
                name="password"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.passwordConfirm)}
                    helperText={errors.passwordConfirm?.message}
                    labelText="パスワード確認用"
                    requiredText="必須"
                    required
                    formMb="24px"
                  />
                )}
                control={control}
                name="passwordConfirm"
              />

              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText="担当者名"
                    required
                    requiredText="必須"
                    placeholder="田中太郎"
                    formMb="24px"
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText="部署"
                    required
                    requiredText="必須"
                    placeholder="マーケティング"
                    formMb="24px"
                  />
                )}
                control={control}
                name="department"
              />
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[styles.submitButton]}
                onClick={(): void => {
                  void (async (): Promise<void> => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (iconSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                  登録する
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={(): void => setOpen(false)}
            text={errorText}
            sx={styles.snackbar}
          />
        </Box>
      </Box>
    </>
  );
};

export default StaffRegisterCard;
